<template>
    <div>
        <vrm-header>
            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task visible-lg">
                        <button class="sg-btn sg-blue" @click.prevent="openCreateModal">
                            {{
                                $t('accesstokens.add', {
                                    i18nFallback: 'Add',
                                })
                            }}
                        </button>
                    </div>
                </div>
            </template>
        </vrm-header>
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    <span data-i18n="accesstokens.active_tokens_header">
                        {{
                            $t('accesstokens.active_tokens_header', {
                                i18nFallback: 'Active personal access tokens',
                            })
                        }}
                    </span>
                </h1>
            </template>
            <template v-slot:header-controls>
                <div class="vrm-tasks">
                    <div class="vrm-tasks__task visible-lg">
                        <button class="sg-btn sg-blue" @click.prevent="openCreateModal">
                            {{
                                $t('accesstokens.add', {
                                    i18nFallback: 'Add',
                                })
                            }}
                        </button>
                    </div>
                </div>
            </template>
        </vrm-title>
        <div class="container">
            <p
                v-html="
                    $t('accesstokens.add_token_copy', {
                        i18nFallback:
                            'You can use personal access tokens to authenticate against VRM over HTTP. They are the only accepted password when you have Two-Factor Authentication (2FA) enabled.',
                    })
                "
            ></p>
            <div class="sg-btn__container--align-right hidden-lg">
                <button class="sg-btn sg-blue" @click.prevent="openCreateModal">
                    {{
                        $t('accesstokens.add', {
                            i18nFallback: 'Add',
                        })
                    }}
                </button>
            </div>
            <p v-if="loading" class="sg-color-blue">
                {{ $t('core.loading', { i18nFallback: 'Loading...' }) }}
            </p>
            <p v-else-if="!tokens.length" class="sg-color-blue">
                {{
                    $t('accesstokens.no_tokens', {
                        i18nFallback: 'You have not created any tokens yet.',
                    })
                }}
            </p>
            <br />
            <vrm-scroll-fade>
                <table class="vrm-table">
                    <thead>
                        <tr>
                            <th>{{ $t('accesstokens.name', { i18nFallback: 'Name' }) }}</th>
                            <th class="vrm-table__nowrap">
                                {{ $t('accesstokens.created', { i18nFallback: 'Created on' }) }}
                            </th>
                            <th class="vrm-table__nowrap">
                                {{ $t('accesstokens.expires', { i18nFallback: 'Expires on' }) }}
                            </th>
                            <th class="vrm-table__nowrap">
                                {{ $t('accesstokens.last_seen', { i18nFallback: 'Last seen' }) }}
                            </th>
                            <th class="vrm-table__nowrap">
                                {{
                                    $t('accesstokens.last_successful_auth', {
                                        i18nFallback: 'Last successful authentication',
                                    })
                                }}
                            </th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="token in tokens" :key="token.idAccessToken">
                            <td>{{ token.name }}</td>
                            <td class="vrm-table__nowrap">{{ formatDate(token.createdOn) }}</td>
                            <td class="vrm-table__nowrap">{{ formatDate(token.expires) }}</td>
                            <td class="vrm-table__nowrap">{{ formatDateTime(token.lastSeen) }}</td>
                            <td class="vrm-table__nowrap">
                                {{ formatDateTime(token.lastSuccessfulAuth) }}
                            </td>
                            <td class="vrm-table__actions">
                                <a
                                    class="vrm-table__icon--link"
                                    @click="confirmDelete(token.idAccessToken)"
                                >
                                    <i class="sg-vrm-icon_055_cross-16px" />
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </vrm-scroll-fade>
        </div>
    </div>
</template>
<script>
import { Vue, Component } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import VrmHeaderSearchPanel from '@/vue/components/molecules/VrmHeaderSearchPanel';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal';
import VrmToggleButton from '@/vue/components/shared/toggles/VrmToggleButton';
import AccessTokenCreateModal from '@/vue/components/modals/AccessTokenCreateModal';
import VrmScrollFade from '../shared/VrmScrollFade.vue';

import {
    NOTIFICATION_TYPE_ERROR,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATIONS,
    VrmNotification,
} from '@/vue/store/modules/notifications';
@Component({
    components: {
        VrmToggleButton,
        VrmHeader,
        VrmHeaderSearchPanel,
        VrmScrollFade,
        VrmTitle,
    },
})
@AngularRoute('base.manage-access-tokens', '/access-tokens', { slot: 'main' })
@AngularLocaleBundle('core')
@AngularLocaleBundle('common')
@AngularLocaleBundle('accesstokens')
export default class ManageAccessTokens extends Vue {
    /** @type InstallationGroupsService */
    @ngInject()
    VrmModalService;

    /** @type AccessTokensService */
    @ngInject()
    AccessTokensService;

    loading = true;
    search = {
        active: false,
        query: '',
    };

    localUser = null;

    tokens = [];

    async created() {
        try {
            this.loading = true;
            await this.loadTokens();
        } finally {
            this.loading = false;
        }
    }

    async loadTokens() {
        const resp = await this.AccessTokensService.requestActiveTokens();

        if (resp.success) {
            this.tokens = resp.tokens;
        }
    }

    async confirmDelete(idToken) {
        const props = {
            item: this.$t('accesstokens.access_token', { i18nFallback: 'Access Token' }),
            message: this.$t('accesstokens.confirm_delete_description', {
                i18nFallback:
                    'Are you sure you want to delete this access token? Any applications using this token will lose access and this action cannot be undone.',
            }),
        };

        // Confirm with the user before deleting any access tokens
        if (await this.VrmModalService.openVueModal(ConfirmDeletionModal, props)) {
            const success = await this.deleteToken(idToken);
            await this.loadTokens();

            let notification;
            if (success) {
                notification = new VrmNotification({
                    type: NOTIFICATION_TYPE_SUCCESS,
                    title: this.$t('accesstokens.notification_revoke', {
                        i18nFallback: 'The access token has been revoked successfully.',
                    }),
                });
            } else {
                notification = new VrmNotification({
                    type: NOTIFICATION_TYPE_ERROR,
                    title: this.$t('accesstokens.error_revoking_token', {
                        i18nFallback: 'Something went wrong while revoking your access token.',
                    }),
                });
            }

            this.$store.dispatch(NOTIFICATIONS.ADD, {
                notification: notification,
            });
        }
    }

    async deleteToken(idToken) {
        let resp = await this.AccessTokensService.revokeToken(idToken);
        return resp.success;
    }

    async openCreateModal() {
        const props = {};

        let resp = await this.VrmModalService.openVueModal(AccessTokenCreateModal, props);

        if (resp !== false) {
            await this.loadTokens();
        }
    }

    formatDate(timestamp) {
        if (!timestamp) {
            return '-';
        }

        // Multiply by 1000 since Date expects timestamp in milliseconds and API gives a timestamp in seconds.
        const date = new Date(parseInt(timestamp) * 1000);
        return (
            date.getFullYear() +
            '-' +
            this._formatDatePart(date.getMonth() + 1) +
            '-' +
            this._formatDatePart(date.getDate())
        );
    }

    formatDateTime(timestamp) {
        if (!timestamp) {
            return '-';
        }

        // Multiply by 1000 since Date expects timestamp in milliseconds and API gives a timestamp in seconds.
        const date = new Date(parseInt(timestamp) * 1000);
        return (
            date.getFullYear() +
            '-' +
            this._formatDatePart(date.getMonth() + 1) +
            '-' +
            this._formatDatePart(date.getDate()) +
            ' ' +
            this._formatDatePart(date.getHours()) +
            ':' +
            this._formatDatePart(date.getMinutes())
        );
    }

    /**
     * Make sure the date parts are always formatted as 2 digits.
     *
     * E.g. 2023-01-01
     *
     * @param part
     * @returns {string}
     * @private
     */
    _formatDatePart(part) {
        return part < 10 ? `0${part}` : `${part}`;
    }
}
</script>
